import type { JSX } from 'solid-js'
import { For, createEffect, onMount } from 'solid-js'
import { Check } from 'lucide-solid'

export const Stepper = (props: {
  steps: string[]
  active: number  // 0-based
  onStepClick?: (i: number) => void
}): JSX.Element => {
  let listRef: HTMLOListElement | undefined
  const itemRefs: (HTMLLIElement | undefined)[] = []

  const scrollActiveIntoView = () => {
    const el = itemRefs[props.active]
    if (!el || !listRef) return
    // scrollIntoView is missing in JSDOM; guard so tests don't fail.
    if (typeof el.scrollIntoView !== 'function') return
    el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
  }

  onMount(scrollActiveIntoView)
  createEffect(() => {
    void props.active
    scrollActiveIntoView()
  })

  return (
    <ol
      ref={listRef}
      class="flex items-center gap-2 w-full overflow-x-auto overscroll-x-contain scroll-smooth px-2 py-2 [scroll-padding-inline:1rem] [&::-webkit-scrollbar]:hidden"
      style={{ 'scrollbar-width': 'none' }}
    >
      <For each={props.steps}>
        {(label, idx) => {
          const state = () => {
            const i = idx()
            return i < props.active ? 'done' : i === props.active ? 'active' : 'pending'
          }
          return (
            <>
              <li
                ref={(el) => (itemRefs[idx()] = el)}
                data-state={state()}
                onClick={() => props.onStepClick?.(idx())}
                class="flex items-center gap-2 shrink-0 cursor-pointer data-[state=pending]:cursor-default"
              >
                <span
                  data-state={state()}
                  class={
                    'w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ' +
                    'data-[state=done]:bg-accent data-[state=done]:text-white ' +
                    'data-[state=active]:bg-accent-soft data-[state=active]:text-accent-strong data-[state=active]:ring-2 data-[state=active]:ring-accent ' +
                    'data-[state=pending]:bg-surface-alt data-[state=pending]:text-muted'
                  }
                >
                  {state() === 'done' ? <Check size={14} /> : idx() + 1}
                </span>
                <span class={'text-sm whitespace-nowrap ' + (state() === 'active' ? 'text-fg font-medium' : 'text-muted')}>{label}</span>
              </li>
              {idx() < props.steps.length - 1 && (
                <li class="w-8 shrink-0 h-px bg-border" aria-hidden />
              )}
            </>
          )
        }}
      </For>
    </ol>
  )
}

